import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  type FetchCache,
} from '@/store/fetchCache'

import { agentsService } from '../services/agentsService.http'
import type { AgentView } from '../types'

const rejected = (error: unknown): ApiError =>
  isApiError(error) ? error : toApiError(error)

export interface AgentsState extends FetchCache {
  byId: Record<string, AgentView>
  allIds: string[]
  requestedOrgId: string | null
  loadedOrgId: string | null
  activeRequestId: string | null
  error: ApiError | null
}

const initialState: AgentsState = {
  byId: {},
  allIds: [],
  requestedOrgId: null,
  loadedOrgId: null,
  activeRequestId: null,
  error: null,
  ...initialFetchCache,
}

/** An organisation switch is a new request identity, never a dropped request. */
export const openAgents = createAsyncThunk<
  AgentView[],
  { orgId: string },
  { rejectValue: ApiError }
>(
  'agents/open',
  async ({ orgId }, { rejectWithValue }) => {
    try {
      return await agentsService.listAgents(orgId)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: ({ orgId }, { getState }) => {
      const state = (getState() as { agents: AgentsState }).agents
      if (state.requestedOrgId !== null && state.requestedOrgId !== orgId)
        return true
      return !state.loading && (state.invalidated || state.loadedKey !== orgId)
    },
  }
)

export const agentsSlice = createSlice({
  name: 'agents',
  initialState,
  reducers: {
    agentsClosed(state) {
      state.byId = {}
      state.allIds = []
      state.requestedOrgId = null
      state.loadedOrgId = null
      state.activeRequestId = null
      state.error = null
      invalidate(state)
    },
    agentsRefetchArmed(state) {
      invalidate(state)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(openAgents.pending, (state, action) => {
        const { orgId } = action.meta.arg
        if (state.requestedOrgId !== orgId) {
          state.byId = {}
          state.allIds = []
          state.loadedOrgId = null
        }
        fetchStarted(state)
        state.requestedOrgId = orgId
        state.activeRequestId = action.meta.requestId
        state.error = null
      })
      .addCase(openAgents.fulfilled, (state, action) => {
        const { orgId } = action.meta.arg
        if (
          state.activeRequestId !== action.meta.requestId ||
          state.requestedOrgId !== orgId
        )
          return
        state.byId = Object.fromEntries(
          action.payload.map((agent) => [agent.id, agent])
        )
        state.allIds = action.payload.map((agent) => agent.id)
        state.loadedOrgId = orgId
        fetchSucceeded(state, orgId)
      })
      .addCase(openAgents.rejected, (state, action) => {
        const { orgId } = action.meta.arg
        if (
          state.activeRequestId !== action.meta.requestId ||
          state.requestedOrgId !== orgId
        )
          return
        fetchFailed(state)
        state.error = action.payload ?? rejected(action.error)
      })
  },
})

export const { agentsClosed, agentsRefetchArmed } = agentsSlice.actions

interface WithAgents {
  agents: AgentsState
}

export const selectAgentsById = (
  state: WithAgents
): Record<string, AgentView> => state.agents.byId
export const selectAgentIds = (state: WithAgents): string[] =>
  state.agents.allIds
export const selectAgents = createSelector(
  [selectAgentsById, selectAgentIds],
  (byId, ids) =>
    ids.flatMap((id) => {
      const agent = byId[id]
      return agent === undefined ? [] : [agent]
    })
)
export const selectAgentById = (
  state: WithAgents,
  agentId: string
): AgentView | undefined => state.agents.byId[agentId]
export const selectAgentsLoading = (state: WithAgents): boolean =>
  state.agents.loading
export const selectAgentsError = (state: WithAgents): ApiError | null =>
  state.agents.error
export const selectAgentsEmpty = (state: WithAgents): boolean =>
  state.agents.loadedOrgId !== null && state.agents.allIds.length === 0
